import type { AxiosRequestConfig } from 'axios';
import { Vector3 } from 'three';
import { type ApiCase, getCase } from '@/api/case';
import { type ApiStudy, getStudy, studyIdFromUrl } from '@/api/study/study';
import { taggedLogger } from '@/util';
import { type ApiBoneId, type FindModelOptions, findStudyModel, getStudyModels } from '@/api/study/studyModels';
import { type ApiStudyLandmarks, getLandmarks, landmarksIdFromUrl } from '@/api/study/studyLandmarks';
import { findLandmark } from '@/domain/anatomy/landmarks';
import { addVectors, multiplyScalar, vector3 } from '@/domain/geometry/vector3';
import { pollTemplate } from '@/api/template/pollTemplate';
import { Side, type SideId } from '@/domain/anatomy/side';
import { templateId } from '@/api/template/templateUrl';
import { type ApiCaseCatStacks, fetchCatStacks } from '@/api/catStacks';
import { catstacksData } from '@/api/catstacksData';
import type { OverlayMeshNode } from '@/views/dicoms/overlay/scene/overlayMesh';
import { type CatstackOverlayNodes } from '@/views/dicoms/overlay/scene/catstackOverlayNodes';
import { overlayCatstacksState } from '@/views/dicoms/overlay/overlayCatstacksState';
import type { OverlayState } from '@/views/dicoms/overlay/overlayState';

const log = taggedLogger('overlay-view');

interface NodesSets {
    coronalNodes: CatstackOverlayNodes;
    sagittalNodes: CatstackOverlayNodes;
    axialNodes: CatstackOverlayNodes;
}

export async function loadData(
    state: OverlayState,
    nodesSets: NodesSets,
    caseId: number,
    config?: AxiosRequestConfig,
): Promise<void> {
    const {
        case_,
        landmarks,
        study,
        catstacks,
    } = await load(caseId, config);

    state.case = {
        caseId: caseId,
        caseUrl: case_.self,
        caseVersion: case_.version,
        operativeSide: case_.side,
    };

    state.sceneCentre = calculateSceneCentre(landmarks, case_.side);
    state.catstacksData = catstacksData(catstacks);
    state.catstacks = overlayCatstacksState(catstacks, landmarks, case_.side, catstacks);

    // Assign bone sources to all view node sets
    await Promise.all([
        assignBoneSources(nodesSets.coronalNodes, study, config),
        assignBoneSources(nodesSets.sagittalNodes, study, config),
        assignBoneSources(nodesSets.axialNodes, study, config),
    ]);

    log.info('Finished loading case');
}

/** Api-data that we load up-front */
type ApiData = {
    case_: ApiCase;
    study: ApiStudy;
    landmarks: ApiStudyLandmarks;
    catstacks: ApiCaseCatStacks;
};

/** Up-front load of data */
async function load(caseId: number, config?: AxiosRequestConfig): Promise<ApiData> {
    log.info('Loading case %d', caseId);
    const case_ = await getCase(caseId, config);

    log.info('Loading surgical-template %d', templateId(case_.manualTemplate).template);

    const template = await pollTemplate(
        case_.manualTemplate,
        (template) => !!template.components,
        config,
    );

    log.info('Loading study %d', studyIdFromUrl(template.study).study);
    const study = await getStudy(template.study, config);

    log.info('Loading landmarks %d', landmarksIdFromUrl(study.landmarks).landmarks);
    const landmarks = await getLandmarks(study.landmarks, config);

    log.info('Loading catstacks for case %d', caseId);
    const catstacks = await fetchCatStacks(caseId, config);
    if (catstacks === null) {
        throw new Error(`Failed to load cat-stacks for case ${ caseId }`);
    }

    return {
        case_,
        study,
        landmarks,
        catstacks,
    };
}

async function assignBoneSources(
    nodes: CatstackOverlayNodes,
    study: ApiStudy,
    config?: AxiosRequestConfig,
): Promise<void> {
    const boneModels = await getStudyModels(study.models, config);

    const assignBoneSource = (
        node: OverlayMeshNode,
        boneId: ApiBoneId,
        options?: FindModelOptions,
    ) => {
        const source = findStudyModel(boneModels, boneId, options);
        node.geometrySource = source !== null ? source.id : null;
    };

    assignBoneSource(nodes.operativeFemur, 'operative-femur');
}

function calculateSceneCentre({ groups }: ApiStudyLandmarks, operative_side: SideId): Vector3 {
    /** As the area of interest is the proximal femur, the scene centre makes sense to be the midpoint
     of the lesser and greater trochanters */

    const lt = operative_side === Side.Left ?
        findLandmark(groups, 'left-femur', 'lt') :
        findLandmark(groups, 'right-femur', 'lt');

    const gt = operative_side === Side.Left ?
        findLandmark(groups, 'left-femur', 'gt') :
        findLandmark(groups, 'right-femur', 'gt');

    return multiplyScalar(addVectors(vector3(lt.coordinates), vector3(gt.coordinates)), 0.5);
}
